import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader, StatusBadge } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const MONTH_LABELS = ["Ian","Feb","Mar","Apr","Mai","Iun","Iul","Aug","Sep","Oct","Noi","Dec"];

export default function Dashboard() {
  const { hasRole, hasModule } = useAuth();
  const [data, setData] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  useEffect(() => { api.get("/planco/dashboard").then(r=>setData(r.data)).catch(()=>{}); }, []);
  useEffect(() => {
    if (!hasRole("admin","hr")) return;
    api.get("/licenses/next-invoice").then(r=>setInvoice(r.data)).catch(()=>setInvoice([]));
  }, [hasRole]);
  if (!data) return <div style={{padding:40}}>Se încarcă...</div>;
  const planco = hasModule("planco");
  const employeeFiles = hasModule("employee_files");
  const timeentries = hasModule("timeentries");
  const sections = data.sections || [];

  return (
    <>
      <PageHeader title="Panou de Control" subtitle={`Anul ${data.year}`} />

      {invoice && invoice.length > 0 && (() => {
        // Valoarea = suma pe toate modulele active; data = prima factură (cronologic) indiferent de modul
        const total = invoice.reduce((acc, it) => acc + Number(it.total_ron || 0), 0);
        const dates = invoice.map(it => it.next_billing_date).filter(Boolean).sort();
        return (
          <div className="card fade-up" style={{padding:"14px 18px", marginBottom:18}} data-testid="next-invoice-widget">
            <div style={{display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
              <div style={{flex:1,minWidth:260}}>
                <div style={{fontSize:11,color:"var(--blue-primary)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em"}}>Valoarea estimativă a următoarei facturi</div>
                <div style={{display:"flex",alignItems:"baseline",gap:14,flexWrap:"wrap",marginTop:2}}>
                  <span className="tabular-nums" style={{fontSize:22,fontWeight:800,color:"var(--navy)"}} data-testid="next-invoice-total">{total} Lei</span>
                  {dates.length > 0 && <span style={{fontSize:13,color:"#475569"}} data-testid="next-invoice-date">Data următoarei facturi: <b>{dates[0]}</b></span>}
                </div>
              </div>
              <button type="button" className="btn btn-secondary" style={{padding:"4px 12px",fontSize:12}} data-testid="next-invoice-details"
                aria-expanded={invoiceOpen} onClick={()=>setInvoiceOpen(o=>!o)}>{invoiceOpen ? "Ascunde detaliile" : "Vezi detalii"}</button>
            </div>
            {invoiceOpen && (
              <div style={{display:"grid",gap:10,marginTop:14}} data-testid="next-invoice-details-list">
                {invoice.map(it => (
                  <div key={it.license_id} style={{padding:"10px 14px",background:"#F5F9FF",borderRadius:6,borderLeft:"3px solid var(--blue-primary)"}}>
                    <div style={{fontSize:11,color:"var(--blue-primary)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em"}}>{it.module_label} · {it.plan_label}</div>
                    <div style={{fontSize:16,fontWeight:700,color:"var(--navy)",marginTop:4}} className="tabular-nums">
                      {it.per_report
                        ? `${it.employment_count} rapoarte de muncă active și suspendate × ${it.unit_price_ron} Lei = ${it.total_ron} Lei`
                        : `Preț fix: ${it.total_ron} Lei`}
                    </div>
                    <div style={{fontSize:12,color:"#475569",marginTop:4}}>
                      Facturare <b>{(it.billing_frequency_label || "").toLowerCase()}</b>
                      {it.next_billing_date && <> — următoarea factură pe <b>{it.next_billing_date}</b></>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {planco && data.campaign && (
        <div className="card fade-up" style={{ padding: 20, marginBottom: 18,
            borderLeft: "3px solid var(--blue-primary)", background: "#F5F9FF" }} data-testid="active-campaign">
          <div style={{fontSize:11,color:"var(--blue-primary)",letterSpacing:"0.05em",textTransform:"uppercase",fontWeight:700}}>Campanie</div>
          <div style={{fontSize:18,fontWeight:700,color:"var(--navy)",marginTop:2}}>{data.campaign.name}</div>
          <div style={{marginTop:8, display:"flex", gap:24, fontSize:13, color:"#334155"}}>
            <span><b>Programare:</b> {data.campaign.access_start} → {data.campaign.access_end}</span>
            <span><b>Aprobare:</b> {data.campaign.approval_co_start} → {data.campaign.approval_co_end}</span>
          </div>
        </div>
      )}

      {sections.map((s, i) => (
        <DashboardSection key={s.role} section={s} planco={planco && s.planco_access !== false} employeeFiles={employeeFiles} timeentries={timeentries} first={i === 0} />
      ))}
    </>
  );
}

function DashboardSection({ section, planco, employeeFiles, timeentries, first }) {
  const counts = section.plan_status_counts || {};
  const maxMonth = Math.max(1, ...Object.values(section.monthly_days || {}));
  return (
    <div style={{ marginTop: first ? 0 : 44 }} data-testid={`dashboard-section-${section.role}`}>
      <h2 data-testid={`dashboard-section-title-${section.role}`}
        style={{ fontSize: 16, fontWeight: 800, color: "#fff", background: "var(--blue-primary)", margin: "0 0 14px",
                 padding: "10px 16px", borderRadius: 8, letterSpacing: "0.01em", boxShadow: "0 1px 3px rgba(0,0,0,.15)" }}>{section.title}</h2>

      <div className="grid-4">
        <StatCard label="Rapoarte de muncă active" value={section.active_employments} tone="navy" testid={`stat-active-employments-${section.role}`} />
        {planco && (
          <>
            <StatCard label="Zile disponibile programare CO" value={section.total_available_days} tone="blue" testid={`stat-total-balance-${section.role}`} />
            <StatCard label="Zile CO programate" value={section.total_planned_days} tone="sky" testid={`stat-planned-${section.role}`} />
            <StatCard label="Programări care așteaptă aprobarea managerului" value={section.pending_approvals} tone="highlight"
              testid={`stat-pending-${section.role}`}
              to={section.role === "employee" ? "/planco/planning" : section.role === "admin_hr" ? "/planco/approvals" : undefined} />
          </>
        )}
      </div>

      {((timeentries && section.unvalidated_timesheets !== undefined) || employeeFiles) && (
        <div className="grid-4" style={{ marginTop: 14 }}>
          {timeentries && section.unvalidated_timesheets !== undefined && (
            <StatCard label="Pontaje nevalidate" value={section.unvalidated_timesheets}
              tone={section.unvalidated_timesheets > 0 ? "highlight" : "navy"}
              testid={`stat-unvalidated-timesheets-${section.role}`} to="/timeentries" />
          )}
          {employeeFiles && (
            <StatCard label="Documente ce expiră" value={section.expiring_documents_count ?? 0}
              tone={(section.expiring_documents_count || 0) > 0 ? "highlight" : "navy"}
              testid={`stat-expiring-documents-${section.role}`}
              to={section.role === "employee" ? "/employee-files" : section.role === "admin_hr" ? "/employee-files/expiring" : undefined} />
          )}
        </div>
      )}

      {planco && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginTop: 18 }}>
          <div className="card fade-up" style={{ padding: 22 }}>
            <h3 style={{ margin: "0 0 14px", fontSize: 15, fontWeight: 700 }}>Status programări</h3>
            <StatusBar counts={counts} />
          </div>
          <div className="card fade-up" style={{ padding: 22 }}>
            <h3 style={{ margin: "0 0 14px", fontSize: 15, fontWeight: 700 }}>Acoperire lunară</h3>
            <div style={{ display: "flex", alignItems: "flex-end", gap: "clamp(2px, 1vw, 8px)", height: 140 }}>
              {Array.from({length:12}, (_,idx) => {
                const v = section.monthly_days?.[idx+1] || 0;
                return (
                  <div key={idx} style={{ flex:1, minWidth:0, display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#334155" }}>{v || ""}</div>
                    <div style={{ width: "100%", height: `${(v/maxMonth)*100}%`, minHeight: 3,
                      background: "linear-gradient(to top, var(--navy-3), var(--blue-bright))", borderRadius: "4px 4px 0 0" }} />
                    <div style={{ fontSize: 10, color: "#64748B", textTransform: "uppercase" }}>{MONTH_LABELS[idx]}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, tone = "navy", testid, to }) {
  const tones = {
    navy: { bg: "white", num: "var(--navy)", bd: "#E2E8F0" },
    blue: { bg: "white", num: "var(--blue-primary)", bd: "#E2E8F0" },
    sky:  { bg: "white", num: "var(--sky-500)", bd: "#E2E8F0" },
    highlight: { bg: "var(--tint-50)", num: "var(--navy-3)", bd: "#93C5FD" },
  };
  const t = tones[tone] || tones.navy;
  const clickable = to && value > 0;
  const content = (
    <>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em",
            color: "#64748B", fontWeight: 700 }}>{label}</div>
        {clickable && <span style={{ fontSize: 13, color: "var(--blue-primary)" }}>→</span>}
      </div>
      <div className="tabular-nums" style={{ fontFamily: "Manrope", fontSize: 34,
          fontWeight: 800, color: t.num, marginTop: 6, letterSpacing: "-0.02em" }}>{value}</div>
    </>
  );
  if (clickable) {
    return (
      <Link to={to} className="card fade-up" data-testid={testid}
        style={{ padding: 22, background: t.bg, borderColor: t.bd, display: "block", textDecoration: "none", cursor: "pointer" }}>
        {content}
      </Link>
    );
  }
  return (
    <div className="card fade-up" data-testid={testid}
      style={{ padding: 22, background: t.bg, borderColor: t.bd }}>
      {content}
    </div>
  );
}

function StatusBar({ counts }) {
  const total = Object.values(counts).reduce((a,b)=>a+b,0) || 1;
  const items = [
    { key: "draft", label: "Ciornă", color: "#94A3B8" },
    { key: "submitted", label: "În așteptare", color: "var(--sky-500)" },
    { key: "approved", label: "Aprobat", color: "var(--blue-primary)" },
    { key: "rejected", label: "Respins", color: "#DC2626" },
  ];
  return (
    <div>
      <div style={{ display:"flex", height:14, borderRadius:7, overflow:"hidden", background:"#F1F5F9" }}>
        {items.map(it => (
          <div key={it.key} style={{ width: `${((counts[it.key]||0)/total)*100}%`, background: it.color }} />
        ))}
      </div>
      <div style={{ marginTop:16, display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
        {items.map(it => (
          <div key={it.key} style={{ display:"flex", alignItems:"center", gap:8, fontSize:13 }}>
            <span style={{ width:10, height:10, background:it.color, borderRadius:2 }} />
            <span style={{ color:"#334155" }}>{it.label}</span>
            <span className="tabular-nums" style={{ marginLeft:"auto", fontWeight:700 }} data-testid={`count-${it.key}`}>{counts[it.key] || 0}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
